import { Permissions } from '../../models/enums/permissions';
import { AnyReport, AnyInventory } from '../../models/enums/permission-groups';
import * as React from 'react';
import { Route, Switch } from 'react-router-dom';
import { hasPermission } from '../../containers/authorization-helpers/has-permission';
import { RequirePermission } from '../../containers/authorization-helpers/RoutePermission';
import { Routes } from '../../utils/app-constants';
import { ConectedLayoutPage } from '../layout/Layout';
import { LegacyRedirects } from './LegacyRedirects';

// Scenes
import { ActivityFeedScene } from '../../pages/communications/activity-feed/views/ActivityFeed';
import { MailTriageScene } from '../../pages/communications/mail-triage/views/MailTriage';
import { DashboardScene } from '../../pages/dashboard/dashboard/views/Dashboard';
import { BackorderScene } from '../../pages/ordering/backorder/views/Backorder';
import { OrdersScene } from '../../pages/ordering/orders/views/Orders';
import { CreditHoldScene } from '../../pages/approvals/credit-hold/views/CreditHold';
import { EngineeringScene } from '../../pages/approvals/engineering/views/Engineering';
import { WindloadScene } from '../../pages/approvals/windload/views/Windload';
import { CustomerServiceScene } from '../../pages/approvals/customer-service/views/CustomerService';
import { FedexScene } from '../../pages/shipping/fedex/views/Fedex';
import { BillingOnlyScene } from '../../pages/invoicing/billing-only/views/BillingOnly';
import { CreditEntryScene } from '../../pages/invoicing/credits/views/CreditEntry';
import { InvoiceSearchScene } from '../../pages/invoicing/invoices/views/InvoiceSearch';
import { ForbiddenScene } from '../../pages/utility/forbidden/views/Forbidden';
import { NotFoundScene } from '../../pages/utility/not-found/views/NotFound';
import { NotReadyScene } from '../../pages/utility/not-ready/views/NotReady';
import { ProductionPlanningScene } from '../../pages/production/planning/views/Planning';
import { ProductionScheduleScene } from '../../pages/production/schedule-cdoem/views/Schedule';
import { ProductionScheduleRsScene } from '../../pages/production/schedule-rs/views/ScheduleRs';
import { ReportsScene } from '../../pages/reports/reports/views/Reports';
import { ActivityScene } from '../../pages/support/activity/views/Activity';
import { DealerDetailScene } from '../../pages/support/dealers/views/DealerDetails';
import { DealersScene } from '../../pages/support/dealers/views/Dealers';
import { DevScene } from '../../pages/support/dev/views/Workbench';
import { UserDetailsScene } from '../../pages/support/users/views/EditUser';
import { UserPreferences } from '../../pages/support/users/views/UserPreferences';
import { UsersScene } from '../../pages/support/users/views/Users';
import { SyncScene } from '../../pages/ordering/sync/views/Sync';
import { FleetSetupScene } from '../../pages/shipping/fleet/views/FleetSetup';
import { RoutingScene } from '../../pages/routing/routing/views/Routing';
import { AddressesScene } from '../../pages/approvals/addresses/views/Addresses';
import { AddressApprovalScene } from '../../pages/approvals/addresses/views/AddressApproval';
import { EditContainerScene } from '../../pages/shipping/containers/views/ContainerEdit';
import { ContainersScene } from '../../pages/shipping/containers/views/Containers';
import { EditPickupScene } from '../../pages/shipping/pickups/views/EditPickup';
import { PickupsScene } from '../../pages/shipping/pickups/views/Pickups';
import { EditTruckScene } from '../../pages/shipping/trucks/views/TruckEdit';
import { TrucksScene } from '../../pages/shipping/trucks/views/Trucks';
import { CommunicationsScene } from '../../pages/communications/shipments/views/CommunicationsScene';
import { InventoryPhysicalScene } from '../../pages/inventory/physical/views/InventoryPhysicalList';
import { InventoryPhysicalDetailsScene } from '../../pages/inventory/physical/views/InventoryPhysicalDetails';

const pickupsRead = () => Permissions.ShippingPickupsView;
const trucksRead = () => Permissions.ShippingTrucksView;
const containersRead = () => Permissions.ShippingContainersView;

/**
 * Pickups, trucks and containers used to be tabs on one shipments page.  Anyone
 * who still has that address gets the first one they are allowed to see.
 */
const firstShipmentsPage = () => {

    if (hasPermission({ requiredPermissions: [trucksRead()] })) {
        return Routes.SHIPPING_TRUCKS;
    }
    else if (hasPermission({ requiredPermissions: [containersRead()] })) {
        return Routes.SHIPPING_CONTAINERS;
    }
    else if (hasPermission({ requiredPermissions: [pickupsRead()] })) {
        return Routes.SHIPPING_PICKUPS;
    }

    return '/';
};

export const RouteTable = (props: any) => (
    <Route render={({ location }) => (
        <div>
            <Switch location={location}>
                <ConectedLayoutPage>
                    {/*  ************************************** */}
                    {/* Dashboard Routes */}
                    {/*  ************************************** */}
                    <Route
                        key="dashboard"
                        exact={true}
                        path={Routes.DASHBOARD}
                        component={RequirePermission(DashboardScene, [Permissions.Dashboard])} />

                    {/*  ************************************** */}
                    {/* Reports Routes */}
                    {/*  ************************************** */}
                    <Route
                        key="reports"
                        path={Routes.REPORTS_REPORTS}
                        component={RequirePermission(ReportsScene, [
                            ...AnyReport
                        ])} />

                    {/*  ************************************** */}
                    {/* Ordering Routes */}
                    {/*  ************************************** */}
                    <Route
                        key="ordering-orders"
                        exact={true}
                        path={Routes.ORDERING_ORDERS}
                        component={RequirePermission(OrdersScene, [
                            Permissions.OrderingOrders
                        ])}
                    />

                    <Route
                        key="ordering-backorder"
                        exact={true}
                        path={Routes.ORDERING_BACKORDER}
                        component={RequirePermission(BackorderScene, [
                            Permissions.OrderingBackorder
                        ])}
                    />

                    <Route
                        key="ordering-sync"
                        exact={true}
                        path={Routes.ORDERING_SYNC}
                        component={RequirePermission(SyncScene, [
                            Permissions.OrderingSync
                        ])}
                    />

                    {/*  ************************************** */}
                    {/* Approvals Routes */}
                    {/*  ************************************** */}
                    <Route
                        key="approvals-credit-hold"
                        exact={true}
                        path={Routes.APPROVALS_CREDIT_HOLD}
                        component={RequirePermission(CreditHoldScene, [
                            Permissions.ApprovalsCreditHoldView
                        ])}
                    />

                    <Route
                        key="approvals-engineering"
                        exact={true}
                        path={Routes.APPROVALS_ENGINEERING}
                        component={RequirePermission(EngineeringScene, [
                            Permissions.ApprovalsEngineering
                        ])}
                    />

                    <Route
                        key="approvals-windload"
                        exact={true}
                        path={Routes.APPROVALS_WINDLOAD}
                        component={RequirePermission(WindloadScene, [
                            Permissions.ApprovalsWindLoad
                        ])}
                    />

                    <Route
                        key="approvals-customer-service"
                        exact={true}
                        path={Routes.APPROVALS_CUSTOMER_SERVICE}
                        component={RequirePermission(CustomerServiceScene, [
                            Permissions.ApprovalsCustomerService
                        ])}
                    />

                    <Route
                        key="approvals-addresses"
                        exact={true}
                        path={Routes.APPROVALS_ADDRESSES}
                        render={RequirePermission(AddressesScene, [
                            Permissions.ApprovalsAddressesView
                        ])}
                    />

                    <Route
                        key="approvals-address-approval"
                        path={`${Routes.APPROVALS_ADDRESSES}/:id`}
                        render={RequirePermission(AddressApprovalScene, [
                            Permissions.ApprovalsAddressesView
                        ])}
                    />

                    {/*  ************************************** */}
                    {/* Routing Routes */}
                    {/*  ************************************** */}
                    <Route
                        key="routing-routing"
                        path={Routes.ROUTING_ROUTING}
                        render={RequirePermission(RoutingScene, [
                            Permissions.RoutingRoutingView
                        ])}
                    />

                    {/*  ************************************** */}
                    {/* Production Routes */}
                    {/*  ************************************** */}
                    <Route
                        key="production-planning"
                        path={Routes.PRODUCTION_PLANNING}
                        render={
                            RequirePermission(ProductionPlanningScene, [
                                Permissions.ProductionMetricsPickupsView,
                                Permissions.ProductionMetricsDeliveriesView,
                            ])
                        }
                    />

                    <Route
                        key="production-schedule"
                        path={Routes.PRODUCTION_SCHEDULE_CDOEM}
                        exact={true}
                        render={
                            RequirePermission(ProductionScheduleScene, [
                                Permissions.ProductionScheduleView
                            ])
                        }
                    />

                    <Route
                        key="production-schedule-rs"
                        path={Routes.PRODUCTION_SCHEDULE_RS}
                        exact={true}
                        render={
                            RequirePermission(ProductionScheduleRsScene, [
                                Permissions.ProductionScheduleRsView
                            ])
                        }
                    />

                    {/*  ************************************** */}
                    {/* Shipping Routes */}
                    {/*  ************************************** */}
                    <Route
                        key="shipping-pickups"
                        exact={true}
                        path={Routes.SHIPPING_PICKUPS}
                        render={RequirePermission(PickupsScene, [pickupsRead()])}
                    />

                    <Route
                        key="shipping-pickups-edit"
                        path={Routes.SHIPPING_PICKUP_EDIT_ROUTE}
                        render={RequirePermission(EditPickupScene, [pickupsRead()])}
                    />

                    <Route
                        key="shipping-trucks"
                        exact={true}
                        path={Routes.SHIPPING_TRUCKS}
                        render={RequirePermission(TrucksScene, [trucksRead()])}
                    />

                    <Route
                        key="shipping-trucks-edit"
                        exact={true}
                        path={Routes.SHIPPING_TRUCK_EDIT_ROUTE}
                        render={RequirePermission(EditTruckScene, [trucksRead()])}
                    />

                    <Route
                        key="shipping-containers"
                        exact={true}
                        path={Routes.SHIPPING_CONTAINERS}
                        render={RequirePermission(ContainersScene, [containersRead()])}
                    />

                    <Route
                        key="shipping-containers-edit"
                        exact={true}
                        path={Routes.SHIPPING_CONTAINER_EDIT_ROUTE}
                        render={RequirePermission(EditContainerScene, [containersRead()])}
                    />

                    <Route
                        key="shipping-fedex"
                        exact={true}
                        path={Routes.SHIPPING_FEDEX}
                        component={RequirePermission(FedexScene, [
                            Permissions.ShippingFedexView
                        ])}
                    />

                    <Route
                        key="shipping-fleet"
                        path={Routes.SHIPPING_FLEET}
                        render={RequirePermission(FleetSetupScene, [
                            Permissions.ShippingFleetAssetsView,
                            Permissions.ShippingFleetDriversView
                        ])}
                    />

                    {/*  ************************************** */}
                    {/* Invoicing Routes */}
                    {/*  ************************************** */}
                    <Route
                        key="invoicing-invoices"
                        exact={true}
                        path={Routes.INVOICING_INVOICES}
                        component={RequirePermission(InvoiceSearchScene, [
                            Permissions.InvoicingInvoices
                        ])}
                    />

                    <Route
                        key="invoicing-credits"
                        exact={true}
                        path={Routes.INVOICING_CREDITS}
                        component={RequirePermission(CreditEntryScene, [
                            Permissions.InvoicingCredits
                        ])}
                    />

                    <Route
                        key="invoicing-billing-only"
                        exact={true}
                        path={Routes.INVOICING_BILLING_ONLY}
                        component={RequirePermission(BillingOnlyScene, [
                            Permissions.InvoicingBillingOnlyView
                        ])}
                    />

                    {/*  ************************************** */}
                    {/* Communications Routes */}
                    {/*  ************************************** */}
                    <Route
                        key="communications-shipments"
                        path={Routes.COMMUNICATIONS_SHIPMENTS}
                        render={RequirePermission(CommunicationsScene, [
                            Permissions.CommunicationsShipmentsTrucksView,
                            Permissions.CommunicationsShipmentsContainersView
                        ])}
                    />

                    <Route
                        key="communications-mail-triage"
                        exact={true}
                        path={Routes.COMMUNICATIONS_MAIL_TRIAGE}
                        component={RequirePermission(MailTriageScene, [
                            Permissions.CommunicationsMailTriageView
                        ])}
                    />

                    <Route
                        key="communications-activity-feed"
                        exact={true}
                        path={Routes.COMMUNICATIONS_ACTIVITY_FEED}
                        component={RequirePermission(ActivityFeedScene, [
                            Permissions.CommunicationsActivityFeedView
                        ])}
                    />

                    {/*  ************************************** */}
                    {/* Inventory Routes */}
                    {/*  ************************************** */}
                    <Route
                        key="inventory-physical"
                        exact={true}
                        path={Routes.INVENTORY_PHYSICAL}
                        component={RequirePermission(InventoryPhysicalScene, [
                            ...AnyInventory
                        ])}
                    />

                    <Route
                        key="inventory-physical-details"
                        path={Routes.INVENTORY_PHYSICAL_DETAILS_WITH_ROUTE}
                        render={RequirePermission(InventoryPhysicalDetailsScene, [
                            ...AnyInventory
                        ])}
                    />

                    {/*  ************************************** */}
                    {/* Support Routes */}
                    {/*  ************************************** */}
                    <Route
                        key="support-dealers"
                        exact={true}
                        path={Routes.SUPPORT_DEALERS}
                        component={RequirePermission(DealersScene, [
                            Permissions.SupportDealersView
                        ])}
                    />

                    <Route
                        key="support-dealers-edit"
                        path={Routes.SUPPORT_DEALERS_WITH_ROUTE}
                        render={RequirePermission(DealerDetailScene, [
                            Permissions.SupportDealersView
                        ])}
                    />

                    <Route
                        key="support-users"
                        exact={true}
                        path={Routes.SUPPORT_USERS}
                        component={RequirePermission(UsersScene, [
                            Permissions.SupportUsersView
                        ])}
                    />

                    <Route
                        key="support-users-edit"
                        exact={true}
                        path={Routes.SUPPORT_USER_EDIT_ROUTE}
                        component={RequirePermission(UserDetailsScene, [
                            Permissions.SupportUsersView
                        ])}
                    />

                    <Route
                        key="support-users-preferences"
                        path={Routes.SUPPORT_USER_PREFERENCES}
                        component={RequirePermission(UserPreferences, [])}
                    />

                    <Route
                        key="support-activity-feed"
                        exact={true}
                        path={Routes.SUPPORT_ACTIVITY_FEED}
                        component={RequirePermission(ActivityScene, [
                            Permissions.SupportActivityFeed
                        ])}
                    />

                    {/*  ************************************** */}
                    {/* Pages at their old addresses */}
                    {/*  ************************************** */}
                    <LegacyRedirects shipmentsPage={ firstShipmentsPage } />

                    {/*  ************************************** */}
                    {/* Utility Routes */}
                    {/*  ************************************** */}
                    <Route
                        key="forbidden"
                        exact={true}
                        path={Routes.UTILITY_FORBIDDEN}
                        component={ForbiddenScene} />

                    <Route
                        key="not-ready"
                        exact={true}
                        path={Routes.UTILITY_NOT_READY}
                        component={NotReadyScene} />

                    <Route
                        key="not-found"
                        exact={true}
                        path={Routes.UTILITY_NOT_FOUND}
                        component={NotFoundScene} />

                    {/*  ************************************** */}
                    {/* Dev Pages */}
                    {/*  ************************************** */}
                    <Route
                        key="dev-workbench"
                        exact={true}
                        path={Routes.SUPPORT_DEV}
                        component={ DevScene } />

                </ConectedLayoutPage>
            </Switch>
        </div>
    )} />
);
